import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { requireLiveSession } from "@/lib/requireLiveSession";

/**
 * Whether the signed-in user is a Console staff member (a row in
 * public.staff_members). Backed by the SECURITY DEFINER is_staff() RPC. This is
 * orthogonal to the admin/agent role in useUserRole — a staff member may also be
 * an agent-role account (e.g. the founder).
 *
 * Convenience/UX only: it gates nav visibility and route rendering, but the real
 * enforcement is each console-* edge function re-checking membership server-side.
 */
export const useIsStaff = () => {
  const { user, isLoading: authLoading } = useAuth();

  const { data: isStaff = false, isLoading } = useQuery({
    queryKey: ["is-staff", user?.id],
    queryFn: async (): Promise<boolean> => {
      // Outside the try: a skipped call must not be cached as "not staff" (#794).
      await requireLiveSession();
      try {
        // is_staff(_user_id uuid default auth.uid()) — call with no args so it
        // resolves the caller from the JWT.
        const { data, error } = await supabase.rpc("is_staff");
        if (error) {
          console.error("Error checking staff membership:", error);
          return false;
        }
        return data === true;
      } catch (err) {
        console.error("Error checking staff membership:", err);
        return false;
      }
    },
    enabled: !authLoading && !!user,
    // Effectively static within a session; refetches naturally on a different user.
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  return {
    isStaff,
    isLoading: authLoading || isLoading,
  };
};

export type StaffRole = "founder" | "sales" | "support";

/**
 * The signed-in staff member's Console display name (staff_members.display_name),
 * backed by the SECURITY DEFINER staff_display_name() RPC.
 *
 * This is *staff* identity, deliberately separate from the product profile and
 * from user_metadata: the Console chip and the impersonation audit log must name
 * the operator, not whatever public-facing agent persona the same auth user
 * happens to carry.
 *
 * Returns null for non-staff, for staff who haven't set a name, and while
 * loading — callers fall back to the email. Writes go through
 * set_staff_display_name() (self-scoped); staff_members has RLS enabled with no
 * policies, so it is unreachable from the browser except via these RPCs.
 */
export const useStaffProfile = () => {
  const { user, isLoading: authLoading } = useAuth();

  const { data: staffDisplayName = null, isLoading } = useQuery({
    queryKey: ["staff-profile", user?.id],
    queryFn: async (): Promise<string | null> => {
      await requireLiveSession();
      try {
        const { data, error } = await supabase.rpc("staff_display_name");
        if (error) {
          console.error("Error fetching staff display name:", error);
          return null;
        }
        return (data as string | null) ?? null;
      } catch (err) {
        console.error("Error fetching staff display name:", err);
        return null;
      }
    },
    enabled: !authLoading && !!user,
    // Only the staff member themselves can change this, and the editor
    // invalidates the key on save — so a long window costs nothing.
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  return {
    staffDisplayName,
    isLoading: authLoading || isLoading,
  };
};

/**
 * The signed-in staff member's role (founder | sales | support), backed by the
 * SECURITY DEFINER staff_role() RPC. Returns null for non-staff or while loading.
 */
export const useStaffRole = () => {
  const { user, isLoading: authLoading } = useAuth();

  const { data: staffRole = null, isLoading } = useQuery({
    queryKey: ["staff-role", user?.id],
    queryFn: async (): Promise<StaffRole | null> => {
      await requireLiveSession();
      try {
        const { data, error } = await supabase.rpc("staff_role");
        if (error) {
          console.error("Error fetching staff role:", error);
          return null;
        }
        return (data as StaffRole | null) ?? null;
      } catch (err) {
        console.error("Error fetching staff role:", err);
        return null;
      }
    },
    enabled: !authLoading && !!user,
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  return {
    staffRole,
    isLoading: authLoading || isLoading,
  };
};
